import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface FeedbackEntry {
  id: string;
  user_id: string | null;
  user_name: string | null;
  user_email: string | null;
  type: string;
  message: string;
  status: string | null;
  created_at: string;
}

export type FeedbackStatus = 'pending' | 'reviewed' | 'addressed' | 'dismissed';
export type FeedbackType = 'bug' | 'feature' | 'general' | 'all';
export type SortField = 'created_at' | 'type' | 'status';
export type SortOrder = 'asc' | 'desc';

export const useAdminFeedback = () => {
  const [feedback, setFeedback] = useState<FeedbackEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<FeedbackType>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  const fetchFeedback = async () => {
    setLoading(true);
    setError(null);

    try {
      let query = supabase
        .from('feedback')
        .select('*')
        .order(sortField, { ascending: sortOrder === 'asc' });

      if (filterType !== 'all') {
        query = query.eq('type', filterType);
      }

      if (filterStatus !== 'all') {
        query = query.eq('status', filterStatus);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) {
        setError(fetchError.message);
        setFeedback([]);
      } else {
        setFeedback(data || []);
      }
    } catch (err) {
      setError('Failed to fetch feedback');
      setFeedback([]);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id: string, status: FeedbackStatus) => {
    try {
      const { error: updateError } = await supabase
        .from('feedback')
        .update({ status })
        .eq('id', id);

      if (updateError) {
        throw updateError;
      }

      setFeedback(prev =>
        prev.map(item =>
          item.id === id ? { ...item, status } : item
        )
      );

      return true;
    } catch (err) {
      console.error('Error updating feedback status:', err);
      return false;
    }
  };

  const deleteFeedback = async (id: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('feedback')
        .delete()
        .eq('id', id);

      if (deleteError) {
        throw deleteError;
      }

      setFeedback(prev => prev.filter(item => item.id !== id));
      return true;
    } catch (err) {
      console.error('Error deleting feedback:', err);
      return false;
    }
  };

  useEffect(() => {
    fetchFeedback();
  }, [filterType, filterStatus, sortField, sortOrder]);

  return {
    feedback,
    loading,
    error,
    filterType,
    setFilterType,
    filterStatus,
    setFilterStatus,
    sortField,
    setSortField,
    sortOrder,
    setSortOrder,
    updateStatus,
    deleteFeedback,
    refetch: fetchFeedback,
  };
};
